import { SearchLg, UsersX } from '@untitled-ui/icons-react/build/cjs';
import { debounce, isEqual } from 'lodash';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import BulkActionBar from 'src/components/BulkActionBar';
import CheckBox from 'src/components/CheckBox';
import CustomPagination from 'src/components/CustomPagination';
import DeleteModal from 'src/components/DeleteModal';
import InputText from 'src/components/InputText/InputText';
import NotFoundUI from 'src/components/NotFoundUI';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TableCmp from 'src/components/TableCmp';
import TableTabButton from 'src/components/TableTabButton';
import { TABLE_IDS } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { removeTeamMember, upsertTeamMembers } from 'src/redux/basic.slice';
import { RootState } from 'src/redux/store';
import { deleteMember, fetchOrganizationMemberList } from 'src/services/OrganizationService';
import { isValidJSON } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import CustomerLoaderRow from './components/CustomerLoaderRow';
import MemberRow from './components/OrgMembersRow';
import InviteTeamMember from './InviteTeamMember';

const initAction = {
    mode: null,
    team: false,
    create_view: false,
    delete_view: false,
    rename_view: false,
    duplicate_view: false,
    data: null,
};

const initParams: Params = {
    search: '',
    sortType: 'desc',
    sortField: 'createdAt',
    page: 1,
    role: '',
    limit: 25,
};

const recordsPerPageArray = [
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
];

const OrganizationMembers = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    //constants
    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);

    // States
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [total, setTotal] = useState(0);
    const [orgMembers, setOrgMembersData] = useState<any[]>([]);
    const [searchApplied, setSearchApplied] = useState<boolean>(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
    const [action, setAction] = useState(initAction);
    const [team, setTeam] = useState<any>({});
    const [params, setParams] = useState(() => {
        const path = location?.state?.from?.split('/');
        const customerDetailLocal = localStorage.getItem('customer-detail');
        localStorage.removeItem('customer-detail');

        if (path?.includes('organization') && path?.length >= 3 && customerDetailLocal) {
            const storedParams = localStorage.getItem(TABLE_IDS.CUSTOMER_LIST);

            return storedParams && isValidJSON(storedParams) ? JSON.parse(storedParams) : initParams;
        } else {
            localStorage.removeItem(TABLE_IDS.CUSTOMER_LIST);

            return initParams;
        }
    });

    const [selectedCustomersForSalesRepReassign, setSelectedCustomersForSalesRepReassign] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [bulkSelection, setBulkSelection] = useState({ isOpen: false, data: [] as number[] });
    const [showAllSelected, setShowAllSelected] = useState(false);

    const selectedMemberIds = bulkSelection.data;

    const setSelectedMemberIds = useCallback((ids: number[]) => {
        if (ids.length === 0) setShowAllSelected(false);
        setBulkSelection({ isOpen: ids.length > 0, data: ids });
    }, []);

    const handleToggleSelectMember = useCallback((id: number) => {
        setBulkSelection(({ data }) => {
            const next = data.includes(id) ? data.filter((i) => i !== id) : [...data, id];
            if (next.length === 0) setShowAllSelected(false);

            return { isOpen: next.length > 0, data: next };
        });
    }, []);

    const [tabsData, setTabsData] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<string | number>('All');
    const [isTabsLoading, setIsTabsLoading] = useState(false);
    const [isShowAction, setIsShowAction] = useState(false);
    // Initialize default tabs
    useEffect(() => {
        if (tabsData.length === 0) {
            setIsTabsLoading(true);
            const defaultTabs = [
                {
                    value: 'All',
                    name: 'All',
                    count: 0,
                },
                {
                    value: 'admin',
                    name: 'Admin',
                    count: 0,
                },
                {
                    value: 'user',
                    name: 'User',
                    count: 0,
                },
            ];

            setTabsData(defaultTabs);
            setIsTabsLoading(false);
        }
    }, []);

    // Fetch data
    const getMembersData = () => {
        setOrgMembersData([]);
        setIsLoading(true);
        setBulkSelection({ isOpen: false, data: [] });
        setShowAllSelected(false);
        fetchOrganizationMemberList(params)
            .then((orgMembersResponse: any) => {
                if (orgMembersResponse?.data) {
                    setOrgMembersData(orgMembersResponse?.data || []);
                }
                setTotal(orgMembersResponse.total ?? 0);
                setIsLoading(false);
            })
            .catch(() => {
                setOrgMembersData([]);
                setTotal(0);
                setIsLoading(false);
            });
    };

    useEffect(() => {
        const controller = new AbortController();
        getMembersData();
        localStorage.setItem(TABLE_IDS.CUSTOMER_LIST, JSON.stringify(params));

        return () => {
            controller.abort();
        };
    }, [params]);

    useEffect(() => {
        if (!isEqual(initParams, params)) {
            setSearchApplied(true);
        } else {
            setSearchApplied(false);
        }
    }, [params]);

    useEffect(() => {
        setSearch(params.search || '');
    }, [params.search]);

    const displayedMembers = useMemo(() => (showAllSelected ? orgMembers.filter((m: any) => selectedMemberIds.includes(m.id)) : orgMembers), [showAllSelected, orgMembers, selectedMemberIds]);

    const headCells = useMemo(() => {
        const isAllSelected = orgMembers.length > 0 && selectedMemberIds.length === orgMembers.length;
        const baseHeadCells = [
            {
                id: 'checkbox',
                name: (
                    <CheckBox
                        id="selectAllMembers"
                        checked={isAllSelected}
                        onChangeFunc={() => setSelectedMemberIds(isAllSelected ? [] : orgMembers.map((m: any) => m.id))}
                        name="selectAllMembers"
                        value="selectAllMembers"
                        disabled={isLoading}
                    />
                ),
                rowClassName: 'w-[40px] min-w-[40px] max-w-[40px] !pr-2 sticky left-0 bg-inherit z-[1]',
                sortable: false,
            },
            {
                id: 'title',
                name: t('Organization.Members_List.Name'),
                rowClassName: 'max-w-px min-w-[240px]',
                sortable: true,
            },
            {
                id: 'role',
                name: t('Organization.Members_List.Role'),
                sortable: true,
                rowClassName: 'max-w-[9.15%] min-w-[140px]',
            },
            {
                id: 'email',
                name: t('Organization.Members_List.Email'),
                sortable: true,
                rowClassName: 'max-w-px min-w-[210px]',
            },
            {
                id: 'lastLoginAt',
                name: t('Organization.Members_List.Last_Active'),
                sortable: true,
                rowClassName: 'max-w-[9.15%] min-w-[200px]',
            },
            {
                id: 'isActive',
                name: t('Organization.Members_List.Status'),
                sortable: true,
                rowClassName: 'max-w-[9.15%] min-w-[170px]',
            },
            {
                id: 'action',
                sortable: false,
                rowClassName: 'w-[64px] min-w-[64px] max-w-[64px] sticky right-0 bg-inherit',
            },
        ];

        return baseHeadCells;
    }, [selectedCustomersForSalesRepReassign, orgMembers]);

    const handleActionType =
        (keyAction: any, mode: any = null, teamRec: any = {}) =>
        () => {
            setTeam(teamRec);
            setAction((old) => ({ ...old, [keyAction]: true, mode }));
        };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        handleInviteTeamMember: () => {
            handleActionType('team', 'member-invite')();
        },
    }));

    const handleDrawerClose = useCallback(() => {
        setAction(initAction);
        setTeam({});
    }, []);

    const handleModalAction = useCallback(
        (status: boolean) => async () => {
            setIsDeleteLoading(true);
            setAction(initAction);
            setTeam({});

            if (status) {
                await deleteMember(team?.id)
                    .then(() => {
                        setOrgMembersData((prev) => prev?.filter((i) => i?.id !== team?.id));
                        // Keep the redux copy that every other page's team-member filter reads in
                        // step, so none of them has to re-fetch /organization/members.
                        dispatch(removeTeamMember(team?.id));
                        WalToast.success(t('Organization.Members_List.Member_removed'));
                    })
                    .catch((e) => {
                        WalToast.error(e?.response?.data?.message ?? t('Organization.Members_List.Delete_Error'));
                    });
            }
            setIsDeleteLoading(false);
        },
        [team]
    );

    const updateMembersData = (data: any = [], isArray = false) => {
        // Mirror every create/edit into the redux copy the quote and order listings read, so this
        // page stays the only one that ever calls /organization/members. Unconditional and ahead
        // of the branches below: those filter the TABLE to the active role tab, while the redux
        // list is the unfiltered master and must keep the member either way.
        dispatch(upsertTeamMembers(data));

        if (isArray && data) {
            setOrgMembersData([...data, ...orgMembers]);
        } else {
            const existing = orgMembers?.find((item) => item?.id === data?.id);
            const newData = { ...existing, ...data };

            const activeRoleFilter = params.role?.toLowerCase();

            if (activeRoleFilter && activeRoleFilter !== 'all') {
                const updatedRole = newData?.role?.toLowerCase();

                if (updatedRole !== activeRoleFilter) {
                    setOrgMembersData((prev) => prev.filter((i) => i?.id !== newData?.id));
                    setTotal((prev) => Math.max(0, prev - 1));

                    return;
                }
            }

            setOrgMembersData((prev) => prev.map((i) => (i?.id == newData?.id ? newData : i)));
        }
    };

    const searchDebounce = useCallback(
        debounce((debSearch: string) => setParams((old: any) => ({ ...old, search: debSearch, page: 1 })), 700),
        []
    );

    const handleFilter = (event: any) => {
        const { value } = event.target;
        setSearch(value);
        searchDebounce(value);
    };

    const handleOutSideClick = () => {
        setIsShowAction(false);
    };

    const handleTabClick = (tab: any) => () => {
        if (tab?.value === activeTab) {
            if (tab?.isIconShow) {
                setIsShowAction((old: any) => !old);
            }

            return;
        } else {
            setIsShowAction(false);

            if (tab?.appliedSorting) {
                setSearch(tab?.appliedFilters?.search || '');
                const updatedParams = {
                    ...params,
                    page: 1,
                    search: tab?.appliedFilters?.search,
                    role: tab?.appliedFilters?.role || '',
                    sortType: tab?.appliedSorting?.sortType,
                    sortField: tab?.appliedSorting?.sortField,
                };
                setParams(updatedParams);
            } else {
                setSearch('');
                const updatedParams = {
                    ...initParams,
                    role: tab.value === 'All' ? '' : tab.value,
                };
                setParams(updatedParams);
            }
            setActiveTab(tab?.value);
        }
    };

    return (
        <>
            <div className={`table-box-wrap w-full flex justify-between flex-col overflow-hidden`}>
                <div className={`transition-all duration-300 ${'h-[45px]'}`}>
                    <div className="table-bottom-header table-header-filter-space !gap-x-2 items-center !border-b border-utilityGray200 !flex-nowrap">
                        <div className="table-header-bottom-left flex flex-1 scrollbar-hide">
                            <div className="flex items-center gap-x-1">
                                <TableTabButton
                                    tabArray={tabsData}
                                    tabParentClassName="whitespace-nowrap scrollbar-hide"
                                    isActive={activeTab}
                                    isTabDataInClick={true}
                                    isShowAction={isShowAction}
                                    tabTextClass="!max-w-[104px] truncate"
                                    handleOnClick={handleTabClick}
                                    handleOutSideClick={handleOutSideClick}
                                    isSkeletonLoading={isTabsLoading}
                                    skeletonCount={3}
                                />
                            </div>
                        </div>

                        <InputText
                            inputName="searchCustomer"
                            size="sm"
                            placeholder={t('Organization.Members_List.Search')}
                            className="bg-white focus:bg-white pl-8 pr-7 placeholder:text-gray500 shadow-sm font-normal search-input"
                            icon={<SearchLg className="absolute top-1/2 -translate-y-1/2 left-2 text-grayText h-4 w-4" />}
                            value={search}
                            inputType="text"
                            isClearable={true}
                            onChangeFunc={handleFilter}
                            parentClassName="table-searchInput max-mdm:flex-1 max-smd:flex-[0_0_100%]  flex-none"
                        />

                        <div className="table-recordsPerPage flex-none">
                            <SelectBox
                                SelectDropdownWrapClass="table-recordsPerPageDropdown"
                                selectLabelWrapClass="table-recordsPerPageSelectValue"
                                name="recordsPerPageGroup"
                                id="recordsPerPageGroup"
                                className="form_control shadow"
                                size="sm"
                                options={recordsPerPageArray}
                                onChangeFunc={(event: any) => {
                                    setParams((old: any) => {
                                        if (old.limit === event.value) {
                                            return old;
                                        }

                                        return {
                                            ...old,
                                            limit: event.value,
                                            page: 1,
                                        };
                                    });
                                }}
                                isSearchable={false}
                                value={recordsPerPageArray.find((val: any) => val.value === params.limit)}
                            />
                        </div>
                    </div>
                </div>

                {/* `relative` anchors the absolutely-positioned BulkActionBar to this container so it
                    overlays the (opacity-0) table header at the right width. Other tables get the same
                    containing block from `draggable-layout-view`/`slide-in`, which carries a transform. */}
                <div data-test-id="organizationMembersTest" className="flex-1 w-full relative">
                    {bulkSelection.isOpen && (
                        <BulkActionBar
                            selectedRows={selectedMemberIds}
                            setSelectedRows={setSelectedMemberIds}
                            allIds={orgMembers.map((m: any) => m.id)}
                            showOnlySelected={showAllSelected}
                            onShowOnlySelectedChange={setShowAllSelected}
                        />
                    )}
                    <div className="overflow-x-auto custom-scrollbar scrollbar-hide">
                        <TableCmp
                            headCells={headCells}
                            params={params}
                            setParams={setParams}
                            tableDataArr={displayedMembers}
                            TableLoaderRowCmp={CustomerLoaderRow}
                            TableRowCmp={MemberRow}
                            isTableDataLoading={isLoading}
                            numberOfSkeletonRows={20}
                            tableHeaderClass=""
                            tableRowHeight="xs"
                            isTableRowClickable={true}
                            isBulkBarOpen={bulkSelection.isOpen}
                            isFirstColumnSticky
                            isActionColumnSticky
                            tableBodyRowClass="bg-white"
                            tableRowCmpProps={{
                                openCustomerPage: (rowData: any) => {
                                    navigate(`${PATH.CUSTOMER_DETAILS}/${rowData?.id}`);
                                },
                                selectedCustomersForSalesRepReassign: selectedCustomersForSalesRepReassign,
                                setSelectedCustomersForSalesRepReassign: setSelectedCustomersForSalesRepReassign,
                                handleActionType: handleActionType,
                                currentUser: currentUser,
                                selectedMemberIds: selectedMemberIds,
                                onToggleSelect: handleToggleSelectMember,
                            }}
                        />
                    </div>
                    {!isLoading && !orgMembers.length && (
                        <NotFoundUI
                            title={searchApplied ? t('Organization.Members_List.No_Members_Found') : t('Organization.Members_List.No_Members_Yet')}
                            desc={searchApplied ? t('Organization.Members_List.No_Members_Found_Desc') : t('Organization.Members_List.No_Members_Added_Yet')}
                            containerClassName="min-h-[calc(100%_-_172.5px)] !h-auto"
                            iconClassName="!hidden"
                            iconSecondaryClassName="!flex"
                            isEmptyBackground={true}
                            iconSecondary={searchApplied ? <SearchLg className="w-5 h-5 text-primary" /> : <UsersX className="w-5 h-5 text-primary" />}
                        />
                    )}
                </div>

                <div className="w-full bg-white rounded-b-xl">
                    <CustomPagination
                        recordsPerPage={params.limit}
                        totalRecords={total}
                        currentPage={params.page}
                        handlePagination={(page: number) => {
                            setParams((old: any) => ({ ...old, page }));
                        }}
                    />
                </div>
            </div>

            {action.mode === 'member-delete' && <DeleteModal moduleName={'Team Member'} handleClose={handleModalAction} primaryBtnText={'Confirm'} isDeleteLoading={isDeleteLoading} />}

            {(action.mode === 'member-invite' || action.mode === 'member-update') && (
                <InviteTeamMember
                    mode={action.mode}
                    team={team}
                    handleClose={(successData: any) => {
                        if (successData) {
                            handleActionType('team', 'team-created-confirmation')();
                        } else {
                            handleDrawerClose();
                        }
                    }}
                    updateMembersData={updateMembersData}
                />
            )}
        </>
    );
});

OrganizationMembers.displayName = 'OrganizationMembers';

export default OrganizationMembers;
